import { css } from "@/utils/css";
import { useAutoPostings } from "@/hooks/use-auto-postings";
import { __ } from "@wordpress/i18n";
import { Spinner } from "@wordpress/components";
import { Center } from "@/components/center";
import { WEEKDAYS } from "@/utils/constants";
import { LuCalendar } from "react-icons/lu";
import { Pill, TimePill } from "@/components/pill";
import { differenceInSeconds, format, formatDate } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { formatDuration } from "@/utils/format";
import { DropdownMenu } from "@wordpress/components";
import { BsThreeDotsVertical } from "react-icons/bs";
import { useModal } from "@/hooks/use-modal";
import { toast } from "react-toastify";
import { api } from "@/config/api-fetch";
import { Table } from "@/components/table";
import { getDates, isValidDate, phpDate } from "@/utils/date";
import { Switch } from "@/components/switch";

const weekdays = WEEKDAYS.map((day) => day.shortLabel);

export function Postings() {
    const { data, isLoading } = useAutoPostings();
    const { openModal } = useModal();

    if (!data?.length) {
        return (
            <Center className={css("p-16")}>
                {isLoading ? (
                    <Spinner className={css("w-6 h-6")} />
                ) : (
                    <div className={css("text-silver text-center text-xl italic font-semibold")}>{__("No postings were found", "antimanual")}</div>
                )}
            </Center>
        );
    }

    return (
        <div className={css("p-8")}>
            <div className={css("postings-grid")}>
                {data.map((posting) => {
                    const more = posting.topics.length - 1;
                    const expDate = phpDate(posting.exp_date);
                    const isExpired = !isValidDate(expDate) || expDate < new Date();
                    const isActive = posting.is_active;

                    return (
                        <div key={posting.post_id} className={css("posting-card flex flex-col justify-between", posting.status)}>
                            <div>
                                <div className={css("posting-header")}>
                                    <div className={css("flex flex-col gap-1")}>
                                        <span className={css("posting-id")}>
                                            {__("ID:", "antimanual")} {posting.post_id}
                                        </span>
                                        <span className={css("status-badge small", posting.status)}>{posting.status}</span>
                                    </div>
                                    <div className={css("flex items-center gap-2")}>
                                        <ToggleStatus postingId={posting.post_id} />
                                        <ActionsMenu postingId={posting.post_id} />
                                    </div>
                                </div>

                                <h3 className={css("leading-lg text-lg font-semibold text-dark")}>
                                    {posting.topics[0] || __("(No Topic)", "antimanual")}
                                </h3>

                                {posting.topics.length > 1 && (
                                    <p
                                        className={css("text-primary cursor-pointer max-w-max")}
                                        onClick={() =>
                                            openModal(
                                                <div className={css("p-6")}>
                                                    <h3>{__("Topics", "antimanual")}</h3>
                                                    <ol>
                                                        {posting.topics.map((topic) => (
                                                            <li key={topic}>{topic}</li>
                                                        ))}
                                                    </ol>
                                                </div>
                                            )
                                        }
                                    >
                                        +{more} {more === 1 ? __("more topic", "antimanual") : __("more topics", "antimanual")}
                                    </p>
                                )}
                            </div>

                            <div>
                                <div className={css("bg-white p-3 rounded-2")}>
                                    <div className={css("schedule-header")}>
                                        <LuCalendar />
                                        <span>{__("Schedule", "antimanual")}</span>
                                    </div>

                                    <div className={css("mb-3")}>
                                        <div className={css("flex flex-wrap gap-1")}>
                                            {weekdays.map((day, idx) => {
                                                const isSelected = posting.weekdays.includes(idx);
                                                return (
                                                    <span key={idx} className={css("weekday-pill", isSelected ? "selected" : "unselected")}>
                                                        {day}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className={css("flex flex-wrap gap-2 mt-4")}>
                                        {posting.times.map((time, i) => (
                                            <TimePill key={i} hour={time.hour} minute={time.minute} />
                                        ))}
                                    </div>
                                </div>
                                {posting.attachments?.length > 0 && (
                                    <div className={css("mt-3 text-gray font-semibold flex items-center gap-2 flex-wrap")}>
                                        <span className={css("text-uppercase")}>{__("Knowledge Files:", "antimanual")}</span>
                                        <ul className={css("m-0 flex items-center gap-2 flex-wrap")}>
                                            {posting.attachments.map((attachment) => {
                                                return (
                                                    <li className={css("m-0")} key={attachment.id}>
                                                        <a className={css("link")} href={attachment.url} target="_blank">
                                                            {attachment.title}
                                                        </a>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                )}
                            </div>

                            <div className={css("mt-4 flex justify-between items-end text-gray font-semibold")}>
                                <div className={css("mt-4 flex flex-col gap-1")}>
                                    <div className={css("flex gap-2 items-center")}>
                                        <span>{__("Type:", "antimanual")}</span> <Pill className={css("post-type")}>{posting.type}</Pill>
                                    </div>
                                    <div className={css("flex gap-2 items-center")}>
                                        <span>{__("Author:", "antimanual")}</span>{" "}
                                        {!posting.author.display_name ? (
                                            __("N/A", "antimanual")
                                        ) : (
                                            <a className={css("link")} href={posting.author.url} target="_blank">
                                                {posting.author.display_name}
                                            </a>
                                        )}
                                    </div>
                                </div>
                                <div className={css("flex flex-col items-start font-semibold text-gray")}>
                                    <span className={css("text-xs")}>{__("Expiration Date", "antimanual")}</span>
                                    <span>{formatDate(expDate, "PPpp")}</span>
                                </div>
                                {isExpired ? (
                                    <span>{__("EXPIRED", "antimanual")}</span>
                                ) : !isActive ? (
                                    <span>{__("PAUSED", "antimanual")}</span>
                                ) : (
                                    <NextRun postingId={posting.post_id} />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function ToggleStatus({ postingId }: { postingId: number }) {
    const { data, refetch } = useAutoPostings();
    const posting = useMemo(() => data?.find((p) => p.post_id === postingId), [data, postingId]);
    const [isActive, setIsActive] = useState<boolean>(posting?.is_active || false);
    const [isToggling, setIsToggling] = useState<boolean>(false);

    if (!posting) return null;

    const expDate = phpDate(posting.exp_date);
    const isExpired = !isValidDate(expDate) || expDate < new Date();

    const handleToggleStatus = async (is_active: boolean) => {
        setIsToggling(true);

        await toast.promise(
            api
                .put(`/auto-posting?auto_posting_id=${posting.post_id}`, { is_active })
                .then(() =>
                    refetch().finally(() => {
                        setIsActive(is_active);
                    })
                )
                .finally(() => setIsToggling(false)),
            {
                pending: __("Updating posting status...", "antimanual"),
                success: __("Posting status updated successfully!", "antimanual"),
                error: __("Failed to update the posting status.", "antimanual"),
            }
        );
    };

    return <Switch checked={isActive} onChange={handleToggleStatus} disabled={isToggling || isExpired} />;
}

function ActionsMenu({ postingId }: { postingId: number }) {
    const { data, refetch } = useAutoPostings();
    const posting = useMemo(() => data?.find((p) => p.post_id === postingId), [data, postingId]);
    const { openModal, confirm } = useModal();

    if (!posting) return null;

    const actions = [
        {
            title: __("Details", "antimanual"),
            onClick: () => {
                openModal(<AutoPostingDetails postingId={posting.post_id} />);
            },
        },
        {
            title: __("Generated Posts", "antimanual") + ` (${posting.posts.length || 0})`,
            onClick: () => {
                openModal(<GeneratedPosts postingId={posting.post_id} />);
            },
        },
        {
            title: __("Trigger Now", "antimanual"),
            onClick: () => {
                toast.promise(
                    api.get(`/auto-posting/trigger?auto_posting_id=${posting.post_id}`).then(() => refetch()),
                    {
                        pending: __("Triggering auto posting...", "antimanual"),
                        success: __("Auto posting triggered successfully!", "antimanual"),
                        error: __("Failed to trigger auto posting.", "antimanual"),
                    }
                );
            },
        },
        {
            title: __("Delete", "antimanual"),
            isDanger: true,
            onClick: async () => {
                const confirmed = await confirm(__("Are you sure you want to delete this auto posting?", "antimanual"));
                if (!confirmed) return;

                toast.promise(
                    api.delete(`/auto-posting?auto_posting_id=${posting.post_id}`).then(() => refetch()),
                    {
                        pending: __("Deleting the auto posting...", "antimanual"),
                        success: __("The auto posting was deleted successfully!", "antimanual"),
                        error: __("Failed to delete the auto posting.", "antimanual"),
                    }
                );
            },
        },
    ];

    return (
        <DropdownMenu icon={<BsThreeDotsVertical className={css("w-5 h-5 text-gray")} />} label={__("Select an action", "antimanual")}>
            {(props) => (
                <ul {...props} className={css("dropdown-menu")}>
                    {actions.map((action) => (
                        <li key={action.title} className={css("menu-item", action.isDanger ? "is-danger" : "")} onClick={action.onClick}>
                            {action.title}
                        </li>
                    ))}
                </ul>
            )}
        </DropdownMenu>
    );
}

function NextRun({ postingId }: { postingId: number }) {
    const { data } = useAutoPostings();
    const posting = useMemo(() => data?.find((p) => p.post_id === postingId), [data, postingId]);

    const [seconds, setSeconds] = useState<number>(0);

    function calculateNextRun() {
        if (!posting) return;

        const now = new Date();
        const dates: Date[] = getDates(8, now);
        const futureRuns: Date[] = [];

        dates.forEach((date) => {
            let runDate = new Date(date.getTime());
            if (!posting.weekdays.includes(runDate.getDay())) return;

            posting.times.forEach((t) => {
                runDate.setHours(t.hour, t.minute, 0, 0);
                if (runDate < now) return;

                futureRuns.push(new Date(runDate.getTime()));
            });
        });

        futureRuns.sort((a, b) => a.getTime() - b.getTime());

        const nextRun = futureRuns[0];

        if (!isValidDate(nextRun)) return null;
        const seconds = differenceInSeconds(nextRun, now);

        return seconds;
    }

    useEffect(() => {
        const interval = setInterval(() => {
            setSeconds(calculateNextRun() || 0);
        }, 500);

        return () => clearInterval(interval);
    }, []);

    if (!posting?.is_active) return null;

    if (seconds < 0) {
        return <div>{__("N/A", "antimanual")}</div>;
    }

    return (
        <div className={css("flex flex-col items-start font-semibold text-gray")}>
            <span className={css("text-xs")}>{__("NEXT RUN", "antimanual")}</span>
            <span className={css("font-mono")}>{formatDuration(seconds)}</span>
        </div>
    );
}

function GeneratedPosts({ postingId }: { postingId: number }) {
    const { data, refetch } = useAutoPostings();

    const posts = useMemo(() => {
        const posting = data?.find((p) => p.post_id === postingId);
        return posting?.posts || [];
    }, [data, postingId]);

    useEffect(() => {
        refetch();
    }, [postingId]);

    if (!posts.length) {
        return (
            <Center className={css("w-64 h-40 p-16")}>
                <p className={css("text-silver italic text-lg font-semibold")}>{__("No posts generated yet.", "antimanual")}</p>
            </Center>
        );
    }

    const columns = [
        {
            id: "title",
            label: __("Title", "antimanual"),
        },
        {
            id: "created_at",
            label: __("Created At", "antimanual"),
        },
        {
            id: "run_date",
            label: __("Scheduled At", "antimanual"),
        },
        {
            id: "post_type",
            label: __("Post Type", "antimanual"),
        },
        {
            id: "status",
            label: __("Status", "antimanual"),
        },
    ];

    const rows = [
        ...posts.map((post) => {
            const created_at = phpDate(post.created_at);
            const run_date = phpDate(post.run_date || "");

            return {
                id: post.id.toString(),
                title: (
                    <div className={css("column")}>
                        {post.title ? (
                            <a href={post.view_link} target="_blank">
                                {post.title || __("(No Title)", "antimanual")}
                            </a>
                        ) : (
                            __("(No Title)", "antimanual")
                        )}

                        {(post.view_link || post.edit_link) && (
                            <div className={css("actions")}>
                                <a href={post.edit_link} target="_blank">
                                    {__("Edit", "antimanual")}
                                </a>
                                |
                                <a href={post.view_link} target="_blank">
                                    {__("View", "antimanual")}
                                </a>
                            </div>
                        )}
                    </div>
                ),
                created_at: <span>{isValidDate(created_at) ? format(created_at, "PPpp") : __("N/A", "antimanual")}</span>,
                run_date: <span>{isValidDate(run_date) ? format(run_date, "PPpp") : __("N/A", "antimanual")}</span>,
                post_type: <Pill className={css("post-type")}>{post.type}</Pill>,
                status: <span className={css("status-badge", post.status)}>{post.status}</span>,
            };
        }),
    ];

    return (
        <div className={css("py-8 px-5 max-w-5xl")}>
            <div className={css("overflow-y-auto")}>
                <h3>{__("Generated Posts", "antimanual")}</h3>
                <Table columns={columns} rows={rows} />
            </div>
        </div>
    );
}

function AutoPostingDetails({ postingId }: { postingId: number }) {
    const { data, refetch } = useAutoPostings();
    const posting = useMemo(() => data?.find((p) => p.post_id === postingId), [data, postingId]);

    useEffect(() => {
        refetch();
    }, [postingId]);

    if (!posting) return null;

    const expDate = phpDate(posting.exp_date);
    const isExpired = !isValidDate(expDate) || expDate < new Date();
    const isActive = posting.is_active;

    const preferences = [
        {
            name: __("TONE", "antimanual"),
            value: posting.tone || __("N/A", "antimanual"),
            class: "max-w-lg col-span-2",
        },
        {
            name: __("Article Language", "antimanual"),
            value: posting.language || __("N/A", "antimanual"),
        },
        {
            name: __("Article Length", "antimanual"),
            value:
                posting.min_length && posting.max_length
                    ? posting.min_length + " - " + posting.max_length + " " + __("words", "antimanual")
                    : __("N/A", "antimanual"),
        },
        {
            name: __("Slug Language", "antimanual"),
            value: posting.slug_language || __("N/A", "antimanual"),
        },
        {
            name: __("Slug Max Length", "antimanual"),
            value: posting.slug_max_length
                ? __("up to") + " " + posting.slug_max_length + " " + __("characters", "antimanual")
                : __("N/A", "antimanual"),
        },
        {
            name: __("Post Type", "antimanual"),
            value: <span className={css("text-uppercase text-gray")}>{posting.type || __("N/A", "antimanual")}</span>,
        },
        {
            name: __("Post Status", "antimanual"),
            value: <span className={css("text-uppercase text-gray")}>{posting.status || __("N/A", "antimanual")}</span>,
        },
        {
            name: __("Author", "antimanual"),
            value:
                posting.author.url && posting.author.display_name ? (
                    <a className={css("link")} href={posting.author.url} target="_blank">
                        {posting.author.display_name}
                    </a>
                ) : (
                    __("N/A", "antimanual")
                ),
        },
        {
            name: __("Expiration Date", "antimanual"),
            value: formatDate(expDate, "PPpp"),
        },
    ];

    return (
        <div className={css("p-8")}>
            <div className={css("flex items-center gap-2 text-dark")}>
                <h2 className={css("m-0")}>{__("Auto Posting Details", "antimanual")}</h2>
                <span className={css("text-gray text-xs")}>
                    ({isExpired ? __("EXPIRED", "antimanual") : isActive ? __("ACTIVE", "antimanual") : __("PAUSED", "antimanual")})
                </span>
            </div>
            <div>
                <h3 className={css("mt-8")}>{__("Topics", "antimanual")}</h3>
                <ol>
                    {posting.topics.map((topic, i) => (
                        <li key={i} className={css("max-w-lg")}>
                            {topic}
                        </li>
                    ))}
                </ol>
            </div>
            <div>
                <h3 className={css("mt-8")}>{__("Preferences", "antimanual")}</h3>
                <div className={css("grid grid-cols-2 gap-4")}>
                    {preferences.map((pref) => {
                        return (
                            <div key={pref.name} className={css(pref.class)}>
                                <strong>{pref.name}: </strong> {pref.value}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
